<template>
  <div class="position-relative w-auto">
    <div style="overflow: auto; background-color: #fff; max-height: 600px; margin-top: 1rem;" ref="tableWrapper">
      <table
        :class="['table', 'scanat-table', 'align-middle',  ...tableClass]"
        :style="{
          tableLayout: 'auto',
          color: '#16191e',
          fontSize: '0.875rem',
          minWidth: minWidth,
          borderCollapse: 'separate',
          borderSpacing: '0',
          ...tableStyle
        }"
      >
        <thead style="position: sticky; top: 0; z-index: 12;">
          <tr :style="{ ...theadTrStyle }">
            <th 
              v-if="selectable" 
              class="selection-column" 
              :class="{
                'lock-left': selectFixed && !isMobileView
              }"
              :style="{
                zIndex: isMobileView ? 'auto' : 3,
                left: (selectFixed && !isMobileView) ? '0px' : 'auto'
              }"
            >
              <div class="form-check">
                <input 
                  type="checkbox" 
                  class="form-check-input" 
                  :checked="isAllSelected" 
                  :indeterminate="isIndeterminate" 
                  @change="toggleSelectAll" 
                />
              </div>
              <div v-if="!isMobileView && isLastLeftFixedColumn(-1)" class="lock-shadow-left"></div>
            </th>
            <th
              v-for="(column, index) in columns"
              :key="index"
              scope="col"
              :class="[
                (column.fixed === 'left' || column.fixed === true) && !isMobileView ? 'lock-left' : '',
                column.fixed === 'right' && !isMobileView ? 'lock-right' : '',
                column.class
              ]"
              :style="{
                left: (column.fixed === 'left' || column.fixed === true) && !isMobileView ? getColumnLeftPosition(index) : undefined,
                right: column.fixed === 'right' && !isMobileView ? `${column.right || 0}px` : undefined,
                maxWidth: column.maxWidth,
                width: column.width,
                minWidth: column.minWidth,
                zIndex: !isMobileView && (((column.fixed === 'left' || column.fixed === true) && index === 0) ||
                        (column.fixed === 'right' && index === columns.length - 1)) ? 3 : undefined,
                ...column.style
              }"
            > 
              <div v-if="column.customHeader">
                <slot
                  :key="`custom-header-${locale}`"
                  :name="`header-${column.prop}`"
                  :row="column"
                  :column="column"
                  :index="index"
                    >
                </slot>
              </div>
              <span v-else>{{ column.title }}</span>
              <div v-if="!isMobileView && isLastLeftFixedColumn(index)" class="lock-shadow-left"></div>
              <div v-if="!isMobileView && isFirstRightFixedColumn(index)" class="lock-shadow-right"></div>
            </th>
          </tr>
        </thead>
        <tbody :style="{
           maxHeight: '20rem',
        }" :class="tbodyClass">
          <template v-if="data && data.length">
            <tr
              v-for="(row, rowIndex) in data"
              :key="rowIndex"
              :style="{ height: rowHeight + 'px', ...tbodyTrStyle }"
              :class="{'row-selected': selectedRowKeys.includes(getRowKey(row, rowIndex))}"
              @click="onRowClick(row, rowIndex, $event)"
            >
              <td 
                v-if="selectable" 
                class="selection-column"
                :class="{
                  'lock-left': selectFixed && !isMobileView
                }" 
                :style="{
                  zIndex: isMobileView ? 'auto' : 3,
                  left: (selectFixed && !isMobileView) ? '0px' : 'auto'
                }"
              >
                <div class="form-check">
                  <input 
                    type="checkbox" 
                    class="form-check-input" 
                    :checked="isRowSelected(row, rowIndex)"
                    :disabled="isRowDisabled(row, rowIndex)"
                    @click.stop
                    @change="toggleRowSelection(row, rowIndex)"
                  />
                </div>
                <div v-if="!isMobileView && isLastLeftFixedColumn(-1)" class="lock-shadow-left"></div>
              </td>
              <td
                v-for="(column, colIndex) in columns"
                :key="colIndex"
                :class="[
                  (column.fixed === 'left' || column.fixed === true) && !isMobileView ? 'lock-left' : '',
                  column.fixed === 'right' && !isMobileView ? 'lock-right' : '',
                  column.dataClass
                ]"
                :style="{
                  left: (column.fixed === 'left' || column.fixed === true) && !isMobileView ? getColumnLeftPosition(colIndex) : undefined,
                  right: column.fixed === 'right' && !isMobileView ? `${column.right || 0}px` : undefined,
                  maxWidth: column.maxWidth,
                  width: column.width,
                  minWidth: column.width,
                  zIndex: !isMobileView && (((column.fixed === 'left' || column.fixed === true) && colIndex === 0) ||
                         (column.fixed === 'right' && colIndex === columns.length - 1)) ? 3 : undefined,
                  ...column.dataStyle
                }"
              >
              <div v-if="column.prop === 'actions'" class="text-ellipsis">
                  <span
                  >
                    <slot
                       name="cell-actions"
                      :row="row"
                      :column="column"
                      :index="rowIndex"
                    >
                      {{ getValueByPath(row, column.prop) }}
                    </slot>
                  </span>
                </div>
                <div v-else :class="column.noEllipsis ? '' : 'text-ellipsis'">
                  <span
                    data-mdb-tooltip-init
                    data-mdb-placement="top"
                    data-mdb-custom-class="tooltip-name"
                  >
                    <slot
                      :name="`cell-${column.prop}`"
                      :row="row"
                      :column="column"
                      :index="rowIndex"
                    >
                      {{ getValueByPath(row, column.prop) }}
                    </slot>
                  </span>
                </div>
                <div v-if="!isMobileView && isLastLeftFixedColumn(colIndex)" class="lock-shadow-left"></div>
                <div v-if="!isMobileView && isFirstRightFixedColumn(colIndex)" class="lock-shadow-right"></div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <div
      v-if="loading"
      class="table-spinner w-100 h-100 position-absolute top-0 left-0 d-flex justify-content-center align-items-center"
      style="z-index: 1200; background-color: rgba(255, 255, 255, 0.6)"
    >
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue'
import { isMobile } from 'scripts/utils.js'
import { useI18n } from 'vue-i18n'
const { locale } =useI18n()
const props = defineProps({
  data: {
    type: Array,
    default: () => []
  },
  columns: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  rowHeight: {
    type: Number,
    default: 50
  },
  minWidth: {
    type: String,
    default: undefined
  },
  tableClass: {
    type: Array,
    default: () => []
  },
  tableStyle: {
    type: Object,
    default: () => ({})
  },
  theadTrStyle: {
    type: Object,
    default: () => ({})
  },
  tbodyClass: {
    type: String,
    default: ''
  },
  tbodyTrStyle: {
    type: Object,
    default: () => ({})
  },
  selectable: {
    type: Boolean,
    default: false
  },
  selectFixed: {
    type: Boolean,
    default: false,
  },
  rowKey: {
    type: String,
    default: 'id'
  },
  defaultSelectedRowKeys: {
    type: Array,
    default: () => []
  },
  disabledRows: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits([
  'select', 
  'select-all', 
  'selection-change', 
  'row-click'
])

const tableWrapper = ref(null)
const isMobileView = ref(isMobile())

const selectedRowKeys = ref([...props.defaultSelectedRowKeys])

const actualColumnHeaderPxWidths = ref({});

const updateActualColumnHeaderPxWidths = () => {
  if (!tableWrapper.value) return;

  const headerRow = tableWrapper.value.querySelector('thead > tr');
  if (!headerRow) return;

  const thElements = headerRow.children; 
  const newWidths = {};

  let currentThDomIndex = 0;

  if (props.selectable) {
    currentThDomIndex = 1; 
  }

  props.columns.forEach((column, columnIndexInProps) => {

    const targetThElement = thElements[ (props.selectable ? 1 : 0) + columnIndexInProps ];
    
    if (targetThElement) {
      newWidths[columnIndexInProps] = targetThElement.getBoundingClientRect().width;
    } 
  });

  actualColumnHeaderPxWidths.value = newWidths;
};


const computedColumnLeftPixelOffsets = computed(() => {
  const offsets = {}; 
  let currentCumulativeWidthPx = 0;


  if (props.selectable && props.selectFixed) {
    const selectionTh = tableWrapper.value?.querySelector('thead > tr > th.selection-column');
    if (selectionTh) {
      currentCumulativeWidthPx = selectionTh.getBoundingClientRect().width;;
    }
  }

  props.columns.forEach((column, colIndexInProps) => {
    if (column.fixed === 'left' || column.fixed === true) {
      offsets[colIndexInProps] = currentCumulativeWidthPx;
      const columnActualWidth = actualColumnHeaderPxWidths.value[colIndexInProps];
      if (typeof columnActualWidth === 'number') {
        currentCumulativeWidthPx += columnActualWidth;
      } 
    }
  });
  return offsets;
});

const getColumnLeftPosition = (colIndex) => {
  const offset = computedColumnLeftPixelOffsets.value[colIndex];
  return typeof offset === 'number' ? `${offset}px` : undefined;
};

const isAllSelected = computed(() => {
  const availableRows = props.data.filter((row, index) => !isRowDisabled(row, index))
  if (availableRows.length === 0) return false
  return availableRows.length > 0 && 
    availableRows.every((row, index) => selectedRowKeys.value.includes(getRowKey(row, index)))
})

const isIndeterminate = computed(() => {
  const availableRows = props.data.filter((row, index) => !isRowDisabled(row, index))
  const selectedRows = availableRows.filter((row, index) => 
    selectedRowKeys.value.includes(getRowKey(row, index))
  )
  return selectedRows.length > 0 && selectedRows.length < availableRows.length
})

const getRowKey = (row, index) => {
  return props.rowKey ? row[props.rowKey] : index
}

const isRowSelected = (row, index) => {
  const key = getRowKey(row, index)
  return selectedRowKeys.value.includes(key)
}

const isRowDisabled = (row, index) => {
  const key = getRowKey(row, index)
  return props.disabledRows.includes(key)
}

const toggleRowSelection = (row, index) => {
  if (isRowDisabled(row, index)) {
    return
  }
  
  const key = getRowKey(row, index)
  const isSelected = selectedRowKeys.value.includes(key)
  
  if (isSelected) {
    selectedRowKeys.value = selectedRowKeys.value.filter(k => k !== key)
  } else {
    selectedRowKeys.value.push(key)
  }
  
  emit('select', selectedRowKeys.value, row, !isSelected)
  emit('selection-change', selectedRowKeys.value)
}

const toggleSelectAll = () => {
  if (isAllSelected.value) {
    selectedRowKeys.value = []
  } else {
    selectedRowKeys.value = props.data
      .filter((row, index) => !isRowDisabled(row, index))
      .map((row, index) => getRowKey(row, index))
  }
  
  emit('select-all', selectedRowKeys.value, !isAllSelected.value)
  emit('selection-change', selectedRowKeys.value)
}

const onRowClick = (row, index, event) => {
  emit('row-click', row, index, event)
}

const lastLeftFixedIndex = computed(() => {
  let lastIndex = -1;
  props.columns.forEach((column, index) => {
    if (column.fixed === 'left' || column.fixed === true) {
      lastIndex = index;
    }
  });
  return lastIndex;
});

const firstRightFixedIndex = computed(() => {
  for (let i = 0; i < props.columns.length; i++) {
    if (props.columns[i].fixed === 'right') {
      return i;
    }
  }
  return -1;
});

const isLastLeftFixedColumn = (index) => {
  return index === lastLeftFixedIndex.value;
};

const isFirstRightFixedColumn = (index) => {
  return index === firstRightFixedIndex.value;
};

const getValueByPath = (object, path) => {
  if (!path) return object
  
  const column = props.columns.find(col => col.prop === path)
  
  if (column && typeof column.formatter === 'function') {
    return column.formatter(object)
  }
  
  return object[path] ?? '—'
}

const checkTableScroll = () => {
  const _table = tableWrapper.value?.querySelector('.scanat-table')
  const _wrapper = tableWrapper.value
  if (_wrapper?.scrollWidth > _wrapper?.offsetWidth) {
   
    _table?.classList?.add('has-scroll')
  } else {
    _table?.classList?.remove('has-scroll')
  }
}

const initTooltips = () => {
  checkTableScroll()
  const root = tableWrapper.value || document
  const tooltips = root.querySelectorAll('[data-mdb-tooltip-init]')
  tooltips.forEach(tooltip => {
    const isTextTruncated = tooltip.scrollWidth > tooltip.clientWidth
    const currentText = (tooltip.innerText || '').trim()
    const tooltipInstance = mdb?.Tooltip?.getInstance(tooltip)

    if (!isTextTruncated) {
      tooltipInstance?.dispose()
      tooltip.removeAttribute('title')
      tooltip.removeAttribute('data-mdb-original-title')
    } else {
      if (tooltip.getAttribute('title') !== currentText) {
        tooltip.setAttribute('title', currentText)
        tooltip.setAttribute('data-mdb-original-title', currentText)
      }
      // Recreate instance to ensure new content is used
      if (tooltipInstance) {
        tooltipInstance.dispose()
      }
      if (mdb?.Tooltip) {
        new mdb.Tooltip(tooltip, {
          container: 'body'
        })
      }
    }
  })
  
}

defineExpose({
  getSelectedRowKeys: () => selectedRowKeys.value,
  getSelectedRows: () => props.data.filter((row, index) => selectedRowKeys.value.includes(getRowKey(row, index))),
  setSelectedRowKeys: (keys) => {
    selectedRowKeys.value = keys
    emit('selection-change', selectedRowKeys.value)
  },
  clearSelection: () => {
    selectedRowKeys.value = []
    emit('selection-change', selectedRowKeys.value)
  }
})

watch(() => props.data, () => {
  console.log('watch')
    requestAnimationFrame(() => {
        const tooltips = document.querySelectorAll('[data-mdb-tooltip-init]')
        const observer = new ResizeObserver(entries => {

          initTooltips()
        });
        tooltips.forEach(e => {
          observer.observe(e)
        })
        nextTick(() => {
          updateActualColumnHeaderPxWidths();
        });
      })
})

watch(() => props.defaultSelectedRowKeys, (newKeys) => {
  selectedRowKeys.value = [...newKeys];
}, { deep: true });

watch(selectedRowKeys, (newSelection) => {
});

watch(() => props.columns, () => {
  nextTick(() => {
    updateActualColumnHeaderPxWidths();
  });
}, { deep: true });

watch([() => props.selectable, () => props.selectFixed], () => {
  nextTick(() => {
    updateActualColumnHeaderPxWidths(); 
  });
});

const handleScroll = () => {
  if (tableWrapper.value) {
    tableWrapper.value.addEventListener('scroll', handleScroll);
    handleScroll();
  }
};

const handleResize = () => {
  isMobileView.value = isMobile();
};

onMounted(() => {
  initTooltips()
  window.addEventListener('resize', initTooltips)
  window.addEventListener('resize', handleResize)

  // New logic for actual column widths
  nextTick(() => {
    updateActualColumnHeaderPxWidths();
  });
  window.addEventListener('resize', updateActualColumnHeaderPxWidths);
})

onUnmounted(() => {
  window.removeEventListener('resize', initTooltips)
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('resize', updateActualColumnHeaderPxWidths); // Cleanup listener
})
</script>

<style scoped>
table tbody tr {
    background-color: #fff !important;
    vertical-align: middle !important;
}

table thead th {
    background-color: rgb(245, 247, 250) !important;
    vertical-align: middle !important;
}

table tbody tr:hover {
  background-color: #f5f7fa !important;
}

table tbody tr:hover .lock-left,
table tbody tr:hover .lock-right {
  background-color: #f5f7fa !important;
}

table.has-scroll .lock-shadow-left {
  position: absolute;
  top: 0;
  right: 14px;
  bottom: 0;
  width: 16px;
  box-shadow: 4px 0px 4px 0px #0000000a;
}
table.has-scroll .lock-shadow-right {
  position: absolute;
  top: 0;
  left: 14px;
  bottom: 0;
  width: 16px;
  box-shadow: -4px 0px 4px 0px #0000000a;
}
.text-ellipsis span {
  width: inherit;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  overflow: hidden;
}
.text-ellipsis span:before {
  content: "";
  display: block;
}

.lock-left {
  position: sticky;
  background-color: #fff;
  overflow: hidden;
  z-index: 2;
}

.lock-right {
  padding-left: 2rem;
  position: sticky;
  background-color: #fff;
  overflow: hidden;
  z-index: 2;
}

.table-spinner {
  position: absolute;
}

.row-selected {
  background-color: #f5f7fa !important;
}

.row-selected td.lock-left,
.row-selected td.lock-right {
  background-color: #f5f7fa !important;
}

.selection-column {
  width: 5rem;
  overflow: visible;
}

.form-check-input {
  cursor: pointer;
}
</style> 